import { useForm, zodResolver } from "@mantine/form";
import {
  Button,
  FileInputField,
  TextInput,
} from "@monolith-forensics/monolith-ui";
import { useState } from "react";
import { GoogleQuery } from "../Google.query";
import { GoogleSaveServiceAccountCredentialsData } from "../Google.types";
import z from "zod";
import { IntegrationScope } from "../../integrations.types";

const CredsSchema = z.object({
  type: z.literal("service_account"),
  project_id: z.string(),
  private_key_id: z.string(),
  private_key: z.string(),
  client_email: z.string(),
  client_id: z.string(),
  auth_uri: z.string(),
  token_uri: z.string(),
  auth_provider_x509_cert_url: z.string(),
  client_x509_cert_url: z.string(),
  universe_domain: z.string(),
});

export const GoogleSaveServiceAccountCredsForm: React.FC<{
  defaultValues?: any;
  onSubmit?: (values: any) => void;
  onSaved?: () => void;
  onCancel?: () => void;
}> = ({ defaultValues, onSubmit, onSaved, onCancel }) => {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { mutate: saveClientCredentials } =
    GoogleQuery.useSaveServiceAccountCredentials();

  const [delegatedAccountEmail, setDelegatedAccountEmail] =
    useState<string>("");
  const [delegatedAccountEmailError, setDelegatedAccountEmailError] = useState<
    string | null
  >(null);
  const [file, setFile] = useState<File | null>(null);

  const form = useForm({
    initialValues: {
      private_key: "",
      client_email: "",
    },
    validate: zodResolver(CredsSchema),
  });

  const handleSave = async () => {
    if (!file) {
      setError("Please select a service account credentials file");
      return;
    }

    if (!delegatedAccountEmail) {
      setDelegatedAccountEmailError("Please enter a delegated account email");
      return;
    }

    setSaving(true);

    const fileName = file.name;
    const jsonData = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const text = e.target?.result as string;
        const json = JSON.parse(text);
        resolve(json);
      };
      reader.readAsText(file);
    });

    const formData = jsonData as GoogleSaveServiceAccountCredentialsData;
    console.log(formData);

    form.setValues(formData);

    const validation = form.validate();
    console.log(validation);

    if (validation.hasErrors) {
      const prettyErrors = Object.entries(validation.errors).map(
        ([key, value]) => `${key}: ${value}`
      );
      alert(prettyErrors.join("\n"));
      setSaving(false);
      return;
    }

    const domain = delegatedAccountEmail.split("@")[1];

    onSubmit?.({
      ...formData,
      file_name: fileName,
      scope: IntegrationScope.TENANT,
      delegated_account_email: delegatedAccountEmail,
      domain,
    });

    saveClientCredentials(
      {
        ...formData,
        file_name: fileName,
        scope: IntegrationScope.TENANT,
        delegated_account_email: delegatedAccountEmail,
        domain,
      },
      {
        onSuccess: () => {
          GoogleQuery.resetQuery();
          setSaving(false);
          onSaved?.();
        },
        onError: () => {
          setSaving(false);
        },
      }
    );
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 10,
      }}
    >
      <TextInput
        label="Delegated Account Email"
        onChange={(e: any) => setDelegatedAccountEmail(e.target.value)}
      />
      <FileInputField
        label="Service Account Credentials File"
        error={error || undefined}
        dropZoneOptions={{
          multiple: false,
          accept: {
            "text/plain": [".json"],
          },
        }}
        onChange={(file) => {
          setFile(file[0] || null);
        }}
      >
        Select or drag and drop your service account credentials file
      </FileInputField>
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          gap: 10,
          justifyContent: "flex-end",
          marginTop: 10,
        }}
      >
        <Button variant="subtle" onClick={onCancel} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSave}
          loading={saving}
        >
          Save
        </Button>
      </div>
    </div>
  );
};
